// Employee management and user dashboard functionality via HTTP API
// 100% HTTP dependent - NO fallbacks, NO hardcoded data

import { get } from '@/api/lib/amplify-wrapper'
import { createImpersonatedApiHeaders, logApiCall } from './api-utils'
import { secureLogger } from '../secure-logger'
import type { DashboardUser } from '../real-data-service'

// HTTP API calls using AWS Amplify client - using monumenta-specific API
// NOTE: This uses /monumenta/employees which returns augmented employee data
// including performance, client assignments, and vacation balance.
// This is different from /workforce/employees which is the base employee data.
const apiCall = async (endpoint: string, params: Record<string, string> = {}): Promise<any> => {
  // const queryParams = new URLSearchParams(params)
  // const path = `/monumenta/employees${endpoint ? `/${endpoint}` : ''}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

  // logApiCall(path)

  // try {
  //   const { response } = await get({
  //     apiName: 'monumenta',
  //     path,
  //     options: {
  //       headers: createImpersonatedApiHeaders()
  //     }
  //   })

  //   const result = await response
  //   return await result.body.json()

  // } catch (error) {
  //   // Enhanced error handling for common issues
  //   if (error instanceof Error) {
  //     if (error.message.includes('CORS') || error.message.includes('Access-Control-Allow-Origin')) {
  //       secureLogger.error('CORS Error: Backend API needs CORS configuration for localhost:3000', {
  //         solution: 'Configure CORS headers on your API Gateway endpoint',
  //         error: error.message
  //       })
  //       throw new Error(`CORS Error: ${error.message}`)
  //     }

  //     if (error.message.includes('NetworkError') || error.message.includes('net::ERR_FAILED')) {
  //       secureLogger.error('Network Error: Cannot reach backend API', {
  //         solution: 'Check if your backend is running and accessible',
  //         error: error.message
  //       })
  //       throw new Error(`NetworkError: ${error.message}`)
  //     }
  //   }

  //   secureLogger.error(`API Call failed for ${path}`, { path, error: error instanceof Error ? error.message : 'Unknown error' })
  //   throw error
  // }
  return {employees: []}
}

// ============================================================================
// 🚀 EMPLOYEE TYPES
// ============================================================================

import type { Employee } from '@/types/workforce'

// Export Employee type from workforce module
export type { Employee }

// MonumentaEmployee is an augmented employee type with additional Monumenta-specific fields
// This comes from the /monumenta/employees endpoint which includes performance data,
// client assignments, and vacation balances in addition to base employee info.
export interface MonumentaEmployee {
  id: number
  employeeId?: string  // Added for impersonation - should map to the workforce employeeId
  name: string
  fullName: string
  email: string
  department: string
  subDepartment: string
  role: string
  admissionDate: string
  contractType: string
  salary: number
  totalCost: number
  vacationBalance: number
  performance: string
  client: string
}

// Function to get all employees with augmented data - 100% HTTP, NO fallbacks
export const getAllMonumentaEmployees = async (): Promise<MonumentaEmployee[]> => {
  const response = await apiCall('', { limit: '200' })

  if (!response.employees) {
    throw new Error('Invalid API response: missing employees array')
  }

  return response.employees
}

// Leadership identification
const LEADERSHIP_KEYWORDS = ['DIRETOR', 'GERENTE', 'COORDENADOR', 'LIDER'];

// Department hierarchy and structure
const DEPARTMENT_HIERARCHY = {
  'PRESIDENCIA': { level: 1, parent: null, color: '#D90A2C' },
  'GESTAO DE PROJETOS': { level: 2, parent: 'PRESIDENCIA', color: '#4760E0' },
  'ADMINISTRATIVO': { level: 2, parent: 'PRESIDENCIA', color: '#10B981' },
  'TECNOLOGIA': { level: 2, parent: 'PRESIDENCIA', color: '#6366F1' },
  'BI': { level: 2, parent: 'PRESIDENCIA', color: '#8B5CF6' },
  'PLANEJAMENTO': { level: 2, parent: 'PRESIDENCIA', color: '#EC4899' },
  'CRIAÇAO': { level: 2, parent: 'PRESIDENCIA', color: '#F59E0B' },
  'PRODUÇAO': { level: 3, parent: 'CRIAÇAO', color: '#EF4444' },
  'UX': { level: 3, parent: 'CRIAÇAO', color: '#06B6D4' },
  'ATENDIMENTO': { level: 3, parent: 'GESTAO DE PROJETOS', color: '#84CC16' },
  'CONTEUDO': { level: 3, parent: 'CRIAÇAO', color: '#F97316' },
  'DADOS': { level: 3, parent: 'BI', color: '#A855F7' },
  'PRODUÇAO FINANCEIRA': { level: 3, parent: 'ADMINISTRATIVO', color: '#14B8A6' },
  'SEO': { level: 3, parent: 'CRIAÇAO', color: '#F43F5E' }
};

// Function to get all employees including duplicates - 100% HTTP, NO fallbacks
export const getAllMonumentaEmployeesWithDuplicates = async (): Promise<MonumentaEmployee[]> => {
  // Deprecated - just return unique employees from HTTP API
  return await getAllMonumentaEmployees()
}

// Function to get leadership keywords
export const getLeadershipKeywords = (): string[] => {
  return LEADERSHIP_KEYWORDS;
};

// Function to get department hierarchy config
export const getDepartmentHierarchyConfig = (): typeof DEPARTMENT_HIERARCHY => {
  return DEPARTMENT_HIERARCHY;
};

// ============================================================================
// 🔗 EMPLOYEE SEARCH AND VALIDATION
// ============================================================================

// Helper function to extract first letter + name part pattern from email
const extractFirstLetterNamePartPattern = (email: string): { firstLetter: string; namePart: string; domain: string } | null => {
  const parts = email.split('@');
  if (parts.length !== 2) return null;
  
  const [localPart, domain] = parts;
  
  // Extract first letter and the rest (which could be any part of the name)
  const firstLetter = localPart.charAt(0).toLowerCase();
  const namePart = localPart.slice(1).toLowerCase();
  
  return { firstLetter, namePart, domain };
};

// Helper function to check if a name matches the first letter + any name part pattern
const nameMatchesPattern = (fullName: string, firstLetter: string, namePart: string): boolean => {
  const nameParts = fullName.trim().split(/\s+/);
  if (nameParts.length < 1) {
    return false;
  }
  
  const firstName = nameParts[0].toLowerCase();
  const firstLetterMatch = firstName.startsWith(firstLetter);

  if (!firstLetterMatch) {
    return false;
  }
  
  // Check if namePart appears in any part of the full name
  const namePartMatches = nameParts.some(part => {
    const partLower = part.toLowerCase();
    // Check for exact match or if the name part contains the search part
    const exactMatch = partLower === namePart;
    const containsMatch = partLower.includes(namePart) && namePart.length >= 3; // Minimum 3 chars for partial match
    const startsWithMatch = partLower.startsWith(namePart) && namePart.length >= 2; // Minimum 2 chars for prefix match
    
    const matches = exactMatch || containsMatch || startsWithMatch;
    return matches;
  });
  
  const isMatch = firstLetterMatch && namePartMatches;

  return isMatch;
};

// Search for employee by email - 100% HTTP, NO fallbacks
export const findUserByFirstLetterLastNameEmail = async (searchEmail: string): Promise<MonumentaEmployee | null> => {
  const employees = await getAllMonumentaEmployees()

  const directMatch = employees.find(emp => emp.email.toLowerCase() === searchEmail.toLowerCase())
  if (directMatch) {
    return directMatch
  }

  return null
}

// Find user by full name - 100% HTTP, NO fallbacks
export const findUserByFullName = async (fullName: string): Promise<MonumentaEmployee | null> => {
  if (!fullName) return null

  const response = await apiCall('', { search: fullName, limit: '5' })

  if (!response.employees) {
    throw new Error('Invalid API response: missing employees array')
  }

  const exactMatch = response.employees.find((emp: MonumentaEmployee) =>
    emp.fullName.toLowerCase() === fullName.toLowerCase() ||
    emp.name.toLowerCase() === fullName.toLowerCase()
  )

  if (exactMatch) {
    return exactMatch
  }

  return null
}

// Email validation function - 100% HTTP, NO fallbacks
export const validateUserEmailExists = async (email: string): Promise<boolean> => {
  const user = await findUserByFirstLetterLastNameEmail(email)
  const isValid = !!user

  return isValid
}

// Validate if a user exists by full name - 100% HTTP, NO fallbacks
export const validateUserFullNameExists = async (fullName: string): Promise<boolean> => {
  const user = await findUserByFullName(fullName)
  const isValid = !!user

  return isValid
}

// Get detailed validation info for debugging - 100% HTTP, NO fallbacks
export const getEmailValidationInfo = async (email: string) => {
  const allEmployees = await getAllMonumentaEmployees()
  const exactMatch = allEmployees.find(emp => emp.email === email)
  const caseInsensitiveMatch = allEmployees.find(emp => 
    emp.email.toLowerCase() === email.toLowerCase()
  )
  
  const patternMatch = await findUserByFirstLetterLastNameEmail(email)
  
  return {
    exactMatch: !!exactMatch,
    caseInsensitiveMatch: !!caseInsensitiveMatch,
    resilientPatternMatch: !!patternMatch,
    patternMatchUser: patternMatch ? { name: patternMatch.name, email: patternMatch.email } : null,
    searchedEmail: email,
    totalEmployees: allEmployees.length,
    allEmails: allEmployees.map(emp => emp.email).slice(0, 10)
  }
}

// Get validation info for a full name - 100% HTTP, NO fallbacks
export const getFullNameValidationInfo = async (fullName: string) => {
  const allEmployees = await getAllMonumentaEmployees()
  const exactFullNameMatch = allEmployees.find(emp => emp.fullName === fullName)
  const caseInsensitiveFullNameMatch = allEmployees.find(emp =>
    emp.fullName.toLowerCase() === fullName.toLowerCase()
  )
  const caseInsensitiveNameMatch = allEmployees.find(emp =>
    emp.name.toLowerCase() === fullName.toLowerCase()
  )
  
  const similarNames = allEmployees
    .filter(emp => 
      emp.fullName.toLowerCase().includes(fullName.toLowerCase()) || 
      emp.name.toLowerCase().includes(fullName.toLowerCase())
    )
    .map(emp => emp.fullName)
    .slice(0, 5)
  
  return {
    searchedName: fullName,
    exactFullNameMatch: !!exactFullNameMatch,
    caseInsensitiveFullNameMatch: !!caseInsensitiveFullNameMatch,
    caseInsensitiveNameMatch: !!caseInsensitiveNameMatch,
    similarNames,
    totalEmployees: allEmployees.length,
    allNames: allEmployees.map(emp => emp.fullName).slice(0, 10)
  }
}

// ============================================================================
// 🚀 UTILITY FUNCTIONS (No global state)
// ============================================================================

// Check if user is leadership
export function isLeadership(user: Employee | MonumentaEmployee): boolean {
  if (!user) return false;
  // Handle both Employee (with nested role) and MonumentaEmployee (flat role)
  const roleName = 'employment' in user ? user.employment.role.name : user.role;
  return getLeadershipKeywords().some(keyword => roleName.toUpperCase().includes(keyword));
}

// Get user's direct reports - accepts employee data explicitly
export async function getDirectReportsForEmployee(employeeEmail: string): Promise<MonumentaEmployee[]> {
  const response = await apiCall(`by-email/${encodeURIComponent(employeeEmail)}/subordinates`)

  if (!response.subordinates) {
    throw new Error('Invalid API response: missing subordinates array')
  }

  return response.subordinates
}

// Get department metrics - accepts department explicitly
export async function getDepartmentMetrics(department: string) {
  try {
    const response = await apiCall('', { department, limit: '200' });
    const deptEmployees = response.employees || [];

    if (deptEmployees.length === 0) return null;

    const totalCost = deptEmployees.reduce((sum: number, emp: MonumentaEmployee) => sum + emp.totalCost, 0);
    const avgSalary = deptEmployees.reduce((sum: number, emp: MonumentaEmployee) => sum + emp.salary, 0) / deptEmployees.length;
    const totalVacationBalance = deptEmployees.reduce((sum: number, emp: MonumentaEmployee) => sum + emp.vacationBalance, 0);

    return {
      department,
      employeeCount: deptEmployees.length,
      totalCost,
      avgSalary,
      totalVacationBalance,
      avgVacationBalance: totalVacationBalance / (deptEmployees.length || 1),
      costPerEmployee: totalCost / deptEmployees.length
    };
  } catch (error) {
    secureLogger.error('Failed to get department metrics', { error: error instanceof Error ? error.message : 'Unknown error' });
    return null;
  }
}

// Get clients for department
export async function getDepartmentClients(department: string): Promise<string[]> {
  try {
    const response = await apiCall('', { department, limit: '200' });
    const deptEmployees = response.employees || [];
    const uniqueClients: string[] = Array.from(new Set(deptEmployees.map((emp: MonumentaEmployee) => emp.client).filter((client: string) => client)));
    return uniqueClients;
  } catch (error) {
    secureLogger.error('Failed to get department clients', { error: error instanceof Error ? error.message : 'Unknown error' });
    return [];
  }
}

// Get dashboard configuration for a specific employee
export async function getDashboardConfigForEmployee(employee: Employee | MonumentaEmployee) {
  try {
    const isManager = isLeadership(employee);
    const departmentName = 'employment' in employee ? employee.employment.department.name : employee.department;

    const [directReports, deptMetrics, userClients] = await Promise.all([
      getDirectReportsForEmployee(employee.email),
      getDepartmentMetrics(departmentName),
      getDepartmentClients(departmentName)
    ]);

    return {
      user: employee,
      isManager,
      directReports,
      departmentMetrics: deptMetrics,
      clients: userClients,
      dashboardType: isManager ? 'manager' : 'employee',
      showTeamManagement: isManager && directReports.length > 0,
      showDepartmentOverview: isManager,
      showPersonalMetrics: true
    };
  } catch (error) {
    secureLogger.error('Failed to get dashboard config', { error: error instanceof Error ? error.message : 'Unknown error' });
    return null;
  }
}

// ============================================================================
// 🔍 EMPLOYEE QUERY FUNCTIONS
// ============================================================================

// Function to get ALL employees including duplicates (for payroll and financial calculations)
export const getAllEmployeesIncludingDuplicates = async (): Promise<MonumentaEmployee[]> => {
  return await getAllMonumentaEmployeesWithDuplicates();
};

// Function to get unique employees only (default for most dashboard views)
export const getAllUniqueEmployees = async (): Promise<MonumentaEmployee[]> => {
  return await getAllMonumentaEmployees();
};

// Get employees by department - 100% HTTP, NO fallbacks
export const getEmployeesByDepartmentLegacy = async (department: string): Promise<MonumentaEmployee[]> => {
  const response = await apiCall('', { department, limit: '200' })

  if (!response.employees) {
    throw new Error('Invalid API response: missing employees array')
  }

  return response.employees
}

// Get all departments - 100% HTTP, NO fallbacks  
export const getAllDepartmentsLegacy = async (): Promise<string[]> => {
  const response = await apiCall('', { limit: '200' })

  if (!response.employees) {
    throw new Error('Invalid API response: missing employees array')
  }

  const departments: string[] = Array.from(new Set(response.employees.map((emp: MonumentaEmployee) => emp.department)))

  return departments.sort()
}

// Get leadership team - 100% HTTP, NO fallbacks
export const getLeadershipTeamLegacy = async (): Promise<MonumentaEmployee[]> => {
  const response = await apiCall('leadership')

  if (!response.leadership) {
    throw new Error('Invalid API response: missing leadership array')
  }

  return response.leadership
}

// ============================================================================
// 🔗 AUTH INTEGRATION
// ============================================================================

// User dashboard integration - fetches dashboard-specific user data
export const integrateUserDashboard = async (userId: string): Promise<DashboardUser> => {
  try {
    const { response } = await get({
      apiName: 'monumenta',
      path: `/users/${userId}/dashboard`,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    });

    const result = await response;
    const data = await result.body.json() as unknown;
    return data as DashboardUser;
  } catch (error) {
    secureLogger.error('Failed to fetch user dashboard', { userId, error: error instanceof Error ? error.message : 'Unknown error' });
    throw new Error('Dashboard integration failed');
  }
};

// Auth integration function - FAIL-FAST: No fallbacks, throw explicit errors with debugging info
export const syncWithAuthUser = async (authUserFullName: string) => {
  try {
    // Attempt to sync user and fetch dashboard data
    const employee = await findUserByFullName(authUserFullName);
    let success = false;
    let dashboardData: DashboardUser | null = null;

    if (employee) {
      try {
        dashboardData = await integrateUserDashboard(String(employee.id));
        success = true;
      } catch (dashError) {
        secureLogger.warn('Dashboard integration failed, continuing with basic sync', {
          employeeId: String(employee.id),
          error: dashError instanceof Error ? dashError.message : 'Unknown error'
        });
        // Continue with sync even if dashboard fails
        success = true;
      }
    }

    if (!success) {
      // If name-based sync fails, provide detailed validation info
      const validationInfo = await getFullNameValidationInfo(authUserFullName);
      throw new Error(
        `🚨 CRITICAL ERROR: User with full name '${authUserFullName}' not found in organizational database.\n\n` +
        `📊 VALIDATION DETAILS:\n` +
        `- Searched full name: ${validationInfo.searchedName}\n` +
        `- Exact full name match: ${validationInfo.exactFullNameMatch ? '✓' : '✗'}\n` +
        `- Case-insensitive full name match: ${validationInfo.caseInsensitiveFullNameMatch ? '✓' : '✗'}\n` +
        `- Case-insensitive name match: ${validationInfo.caseInsensitiveNameMatch ? '✓' : '✗'}\n` +
        `- Similar names found: ${validationInfo.similarNames.length > 0 ? validationInfo.similarNames.join(', ') : 'None'}\n` +
        `- Total employees in database: ${validationInfo.totalEmployees}\n\n` +
        `🔧 POSSIBLE SOLUTIONS:\n` +
        `1. Check if full name format matches exactly (case-sensitive)\n` +
        `2. Verify user exists in employee API\n` +
        `3. Check for typos in the full name\n` +
        `4. Ensure auth system and organizational data are synchronized\n` +
        `5. Try using the shorter name format if available\n\n` +
        `📝 Sample names in database: ${validationInfo.allNames.slice(0, 5).join(', ')}...`
      );
    }

    return true;
  } catch (error) {
    secureLogger.error('Failed to sync auth user', { error: error instanceof Error ? error.message : 'Unknown error' });
    throw error;
  }
};


// ============================================================================
// 🧪 INITIALIZATION AND VALIDATION
// ============================================================================

// Initialize system: Validate HTTP API connectivity but DO NOT set any default user
export const initializeSystemValidation = async () => {
  try {
    await getAllMonumentaEmployees();
  } catch (error) {
    secureLogger.error('Employee system initialization failed', { error: error instanceof Error ? error.message : 'Unknown error' });
  }
};

// Initialize system validation (NO default user) - now async
// NOTE: This should be called AFTER Amplify is configured, not immediately
// initializeSystemValidation();